// HealthTab — sport-only activity tracker ported from Apps/Yawmi/HealthWellness.ts.
// BOUNDARY: secular fitness only. NO Sunnah foods, prophetic medicine, or Islamic
// health quotes — those live in Hisni. This tab is pure sport logging + 7-day chart.
// Storage: aweni_yawmi_sport_log (migrated from yw_sport_log on first mount).
import type { AweniContext } from '../../types';

interface SportEntry { id: string; sportId: string; duration: number; date: string; }

interface SportPreset { id: string; nameKey: string; defaultMin: number; icon: string; cat: 'cardio'|'strength'|'flex'|'team'; }

const LOG_KEY = 'aweni_yawmi_sport_log';
const LEGACY_KEY = 'yw_sport_log';
const MIGRATION_FLAG = 'aweni_yawmi_yw_migration_done';

// Curated sport library (boundary: no Islamic content)
const SPORTS: SportPreset[] = [
  // Cardio
  { id: 'walk',     nameKey: 'yawmi_health_sport_walk',     defaultMin: 30, icon: '🚶', cat: 'cardio' },
  { id: 'run',      nameKey: 'yawmi_health_sport_run',      defaultMin: 20, icon: '🏃', cat: 'cardio' },
  { id: 'bike',     nameKey: 'yawmi_health_sport_bike',     defaultMin: 30, icon: '🚴', cat: 'cardio' },
  { id: 'swim',     nameKey: 'yawmi_health_sport_swim',     defaultMin: 30, icon: '🏊', cat: 'cardio' },
  { id: 'jump',     nameKey: 'yawmi_health_sport_jump',     defaultMin: 10, icon: '🤸', cat: 'cardio' },
  { id: 'dance',    nameKey: 'yawmi_health_sport_dance',    defaultMin: 20, icon: '💃', cat: 'cardio' },
  { id: 'hike',     nameKey: 'yawmi_health_sport_hike',     defaultMin: 60, icon: '🥾', cat: 'cardio' },
  // Strength
  { id: 'pushups',  nameKey: 'yawmi_health_sport_pushups',  defaultMin: 10, icon: '💪', cat: 'strength' },
  { id: 'gym',      nameKey: 'yawmi_health_sport_gym',      defaultMin: 45, icon: '🏋️', cat: 'strength' },
  { id: 'core',     nameKey: 'yawmi_health_sport_core',     defaultMin: 15, icon: '🦵', cat: 'strength' },
  { id: 'plank',    nameKey: 'yawmi_health_sport_plank',    defaultMin: 5,  icon: '🧱', cat: 'strength' },
  // Flexibility
  { id: 'stretch',  nameKey: 'yawmi_health_sport_stretch',  defaultMin: 15, icon: '🧘', cat: 'flex' },
  { id: 'yoga',     nameKey: 'yawmi_health_sport_yoga',     defaultMin: 30, icon: '🧘‍♀️', cat: 'flex' },
  { id: 'pilates',  nameKey: 'yawmi_health_sport_pilates',  defaultMin: 30, icon: '🤸‍♀️', cat: 'flex' },
  // Team / outdoor
  { id: 'football', nameKey: 'yawmi_health_sport_football', defaultMin: 60, icon: '⚽', cat: 'team' },
  { id: 'basket',   nameKey: 'yawmi_health_sport_basket',   defaultMin: 60, icon: '🏀', cat: 'team' },
  { id: 'tennis',   nameKey: 'yawmi_health_sport_tennis',   defaultMin: 45, icon: '🎾', cat: 'team' },
  { id: 'badminton',nameKey: 'yawmi_health_sport_badminton',defaultMin: 30, icon: '🏸', cat: 'team' },
  { id: 'volley',   nameKey: 'yawmi_health_sport_volley',   defaultMin: 45, icon: '🏐', cat: 'team' },
  { id: 'pingpong', nameKey: 'yawmi_health_sport_pingpong', defaultMin: 30, icon: '🏓', cat: 'team' },
];

export class HealthTab {
  private el: HTMLElement;
  private log: SportEntry[] = [];

  constructor(container: HTMLElement) { this.el = container; }

  mount(ctx: AweniContext): void {
    this.migrate();
    try { this.log = JSON.parse(localStorage.getItem(LOG_KEY) || '[]'); } catch { this.log = []; }
    this.render(ctx);
  }

  unmount(): void { this.el.innerHTML = ''; }

  private migrate(): void {
    const flag = localStorage.getItem(MIGRATION_FLAG) || '';
    if (flag.includes('sport')) return;
    try {
      const legacy = localStorage.getItem(LEGACY_KEY);
      if (legacy && !localStorage.getItem(LOG_KEY)) localStorage.setItem(LOG_KEY, legacy);
    } catch { /* best-effort */ }
    localStorage.setItem(MIGRATION_FLAG, (flag ? flag + ',' : '') + 'sport');
  }

  private save(): void { localStorage.setItem(LOG_KEY, JSON.stringify(this.log)); }

  private today(): string { return new Date().toISOString().slice(0, 10); }

  private addEntry(sportId: string, minutes: number): void {
    if (minutes <= 0) return;
    this.log.push({
      id: 'e_' + Math.random().toString(36).slice(2, 10),
      sportId, duration: minutes, date: this.today(),
    });
    // trim: keep last 365 days
    const cutoff = Date.now() - 365 * 86400_000;
    this.log = this.log.filter(e => new Date(e.date).getTime() >= cutoff);
    this.save();
  }

  private deleteEntry(id: string): void {
    this.log = this.log.filter(e => e.id !== id);
    this.save();
  }

  private todayEntries(): SportEntry[] {
    const t = this.today();
    return this.log.filter(e => e.date === t);
  }

  /** Returns [{date, totalMin}] for the last 7 days (oldest first). */
  private weekStats(): Array<{ date: string; total: number }> {
    const out: Array<{ date: string; total: number }> = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      const iso = d.toISOString().slice(0, 10);
      const total = this.log.filter(e => e.date === iso).reduce((s, e) => s + e.duration, 0);
      out.push({ date: iso, total });
    }
    return out;
  }

  private streak(): number {
    let count = 0;
    const today = new Date();
    for (let i = 0; i < 365; i++) {
      const d = new Date(today);
      d.setDate(today.getDate() - i);
      const iso = d.toISOString().slice(0, 10);
      const hasEntry = this.log.some(e => e.date === iso);
      if (hasEntry) count++;
      else if (i > 0) break;
    }
    return count;
  }

  private render(ctx: AweniContext): void {
    const t = (k: string, fb: string) => ctx.i18n.t(k) || fb;
    const today = this.todayEntries();
    const todayTotal = today.reduce((s, e) => s + e.duration, 0);
    const week = this.weekStats();
    const weekMax = Math.max(60, ...week.map(d => d.total));
    const streak = this.streak();
    const sportName = (id: string) => {
      const sp = SPORTS.find(s => s.id === id);
      return sp ? t(sp.nameKey, sp.id) : id;
    };
    const sportIcon = (id: string) => SPORTS.find(s => s.id === id)?.icon || '🏃';

    this.el.innerHTML = `
      <style>
        .ht-summary{display:grid;grid-template-columns:repeat(3,1fr);gap:8px;margin-bottom:12px}
        .ht-stat{background:var(--ln-bg-card,#fff);border:1px solid var(--ln-border,rgba(0,0,0,.08));
          border-radius:var(--ln-radius-md,12px);padding:10px;text-align:center}
        .ht-stat-n{font-size:1.3rem;font-weight:800;color:var(--ln-accent,#0A0A0A)}
        .ht-stat-l{font-size:.7rem;color:var(--ln-text-secondary);text-transform:uppercase;letter-spacing:.5px}
        .ht-section{margin-bottom:12px}
        .ht-title{font-size:.78rem;font-weight:700;color:var(--ln-text-secondary);text-transform:uppercase;letter-spacing:.4px;margin-bottom:6px}
        .ht-week{display:flex;gap:4px;align-items:flex-end;height:80px;padding:8px;background:var(--ln-bg-card,#fff);
          border:1px solid var(--ln-border,rgba(0,0,0,.08));border-radius:var(--ln-radius-md,12px)}
        .ht-week-col{flex:1;display:flex;flex-direction:column;align-items:center;gap:4px;font-size:.65rem;color:var(--ln-text-secondary)}
        .ht-week-track{flex:1;width:100%;display:flex;align-items:flex-end;justify-content:center}
        .ht-week-bar{width:60%;min-height:2px;background:var(--ln-accent,#0A0A0A);border-radius:3px 3px 0 0;transition:height var(--motion-duration-slow,300ms) var(--motion-easing-standard,ease)}
        .ht-week-bar--zero{background:var(--ln-border,rgba(0,0,0,.1))}
        .ht-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(80px,1fr));gap:6px}
        .ht-sport{background:var(--ln-bg-card,#fff);border:1px solid var(--ln-border,rgba(0,0,0,.1));
          border-radius:var(--ln-radius-md,10px);padding:8px 4px;text-align:center;cursor:pointer;font-size:.7rem;
          font-weight:600;color:var(--ln-text-primary,#222);transition:background var(--motion-duration-fast,120ms) ease}
        .ht-sport:hover{background:var(--ln-bg-subtle,rgba(0,0,0,.05))}
        .ht-sport-ic{font-size:1.6rem;display:block;margin-bottom:2px}
        .ht-today{display:flex;flex-direction:column;gap:6px;margin-bottom:12px}
        .ht-entry{display:flex;align-items:center;gap:10px;padding:8px 10px;background:var(--ln-bg-card,#fff);
          border:1px solid var(--ln-border,rgba(0,0,0,.08));border-radius:var(--ln-radius-md,10px)}
        .ht-entry-ic{font-size:1.2rem}
        .ht-entry-info{flex:1;min-width:0}
        .ht-entry-name{font-weight:600;font-size:.9rem}
        .ht-entry-meta{font-size:.72rem;color:var(--ln-text-secondary)}
        .ht-entry-del{background:transparent;border:none;color:#b91c1c;cursor:pointer;font-size:1rem;padding:4px 6px}
        .ht-empty{text-align:center;padding:16px;color:var(--ln-text-secondary);font-size:.85rem;
          background:var(--ln-bg-card,#fff);border:1px dashed var(--ln-border,rgba(0,0,0,.15));border-radius:var(--ln-radius-md,10px)}
      </style>

      <div class="ht-summary">
        <div class="ht-stat"><div class="ht-stat-n">${todayTotal}<small style="font-size:.6em">${escHtml(t('yawmi_health_min','min'))}</small></div><div class="ht-stat-l">${escHtml(t('yawmi_health_today','Today'))}</div></div>
        <div class="ht-stat"><div class="ht-stat-n">${streak}</div><div class="ht-stat-l">${escHtml(t('yawmi_health_streak','Streak (d)'))}</div></div>
        <div class="ht-stat"><div class="ht-stat-n">${week.reduce((s,d)=>s+d.total,0)}</div><div class="ht-stat-l">${escHtml(t('yawmi_health_week_total','Week (min)'))}</div></div>
      </div>

      <div class="ht-section">
        <div class="ht-title">${escHtml(t('yawmi_health_week_view','Last 7 days'))}</div>
        <div class="ht-week" aria-label="${escAttr(t('yawmi_health_week_chart','Weekly activity chart'))}">
          ${week.map(d => {
            const pct = d.total > 0 ? Math.round((d.total / weekMax) * 100) : 0;
            const day = new Date(d.date).toLocaleDateString(ctx.i18n.locale || 'en', { weekday: 'short' });
            return `<div class="ht-week-col">
              <div class="ht-week-track" title="${escAttr(d.date + ': ' + d.total + ' min')}">
                <div class="ht-week-bar${d.total===0?' ht-week-bar--zero':''}" style="height:${pct}%"></div>
              </div>
              <div>${escHtml(day)}</div>
              ${d.total>0 ? `<div style="font-weight:700">${d.total}</div>` : ''}
            </div>`;
          }).join('')}
        </div>
      </div>

      <div class="ht-section">
        <div class="ht-title">${escHtml(t('yawmi_health_today_sports','Today activity'))}</div>
        <div class="ht-today">
          ${today.length === 0
            ? `<div class="ht-empty">${escHtml(t('yawmi_health_no_log','No activity logged today.'))}</div>`
            : today.map(e => `
                <div class="ht-entry" role="listitem">
                  <span class="ht-entry-ic" aria-hidden="true">${sportIcon(e.sportId)}</span>
                  <div class="ht-entry-info">
                    <div class="ht-entry-name">${escHtml(sportName(e.sportId))}</div>
                    <div class="ht-entry-meta">${e.duration} ${escHtml(t('yawmi_health_min','min'))}</div>
                  </div>
                  <button class="ht-entry-del" data-id="${e.id}" aria-label="${escAttr(t('yawmi_health_delete','Delete'))}">✕</button>
                </div>`).join('')}
        </div>
      </div>

      <div class="ht-section">
        <div class="ht-title">${escHtml(t('yawmi_health_log','Log activity'))}</div>
        <div class="ht-grid">
          ${SPORTS.map(s => `
            <button class="ht-sport" data-sport="${s.id}" data-min="${s.defaultMin}"
                    aria-label="${escAttr(t(s.nameKey, s.id))} — ${s.defaultMin} ${escAttr(t('yawmi_health_min','min'))}">
              <span class="ht-sport-ic">${s.icon}</span>
              ${escHtml(t(s.nameKey, s.id))}
              <div style="font-size:.65rem;color:var(--ln-text-secondary);margin-top:2px">${s.defaultMin} ${escHtml(t('yawmi_health_min','min'))}</div>
            </button>`).join('')}
        </div>
      </div>
    `;

    // Sport buttons → quick-log with default duration
    this.el.querySelectorAll<HTMLButtonElement>('.ht-sport').forEach(btn => {
      btn.addEventListener('click', () => {
        const sportId = btn.dataset.sport!;
        const def = parseInt(btn.dataset.min || '30', 10);
        const minutes = parseInt(prompt(t('yawmi_health_log_prompt','How many minutes?') + '', String(def)) || '0', 10);
        if (minutes > 0 && minutes < 1440) {
          this.addEntry(sportId, minutes);
          this.render(ctx);
        }
      });
    });

    // Delete entry
    this.el.querySelectorAll<HTMLButtonElement>('.ht-entry-del').forEach(btn => {
      btn.addEventListener('click', () => {
        this.deleteEntry(btn.dataset.id!);
        this.render(ctx);
      });
    });
  }
}

function escHtml(s: string | undefined | null): string {
  if (s == null) return '';
  return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
function escAttr(s: string | undefined | null): string {
  if (s == null) return '';
  return String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}
